import { resolveFishingTitleReference } from "./title.js";
import {
  buildSearchExpressionFromSelectedTerms,
  cloneSearchExpressionNode,
  compactSearchExpressionNode,
  normalizeSearchExpression,
  normalizeSearchExpressionWithOrigins,
  normalizeSearchExpressionNegated,
  normalizeSearchExpressionOperator,
  searchExpressionNodeHasPatchBound,
} from "./core.js";
import {
  searchExpressionNodeHasEvidenceDateConstraint,
  searchExpressionNodeHasNonDateTerm,
  searchExpressionNodeProvidesEvidenceSampleScopeForEveryBranch,
} from "./applicability.js";
import {
  normalizeFishFilterTerm,
  normalizeFishFilterPrefixTerm,
  normalizeLayerFilterCompareOperator,
  normalizeLayerFilterId,
  normalizeLayerFactCondition,
  normalizeLayerFactConditionOperator,
  normalizeLayerFactConditions,
  normalizeLayerFactId,
  normalizeSearchGradeTerm,
  normalizeSearchStatusTerm,
  normalizePatchBound,
  normalizePatchId,
  normalizeSearchTerm,
  isKnownSourceKind,
  normalizeSourceKind,
  normalizeSourceKinds,
  tradeNpcSearchTermForKey,
} from "./terms.js";
import {
  searchExpressionMissingAliasLength,
  makeCombinedSearchExpressionValueToken,
  searchExpressionTokenLower,
  searchExpressionTokenIsStandaloneOperator,
  searchExpressionTokenIsValue,
  searchExpressionTokenText,
  spanForSearchExpressionToken,
} from "../../js/query/lex.js";
import { SearchExpressionBooleanParser } from "../../js/query/boolean-parser.js";
import {
  parseSearchExpressionPositiveInteger,
  parseSearchExpressionZoneRgb,
  resolveSearchExpressionFishOrItemReference,
  resolveSearchExpressionItemId,
  resolveSearchExpressionPatchId,
  resolveSearchExpressionSemanticField,
  resolveSearchExpressionTradeNpcKey,
  resolveSearchExpressionUnprefixedCatalogTerm,
  resolveSearchExpressionZoneReference,
} from "./resolve.js";
import { parseResolvedRawSearchExpressionDslResult } from "./raw.js";
import {
  projectNormalizedSearchExpressionSourceBindings,
  serializeSearchExpressionSourceBindings,
} from "./source-bindings.js";
import { isPlainObject } from "./shared.js";

export const STRICT_SEARCH_EXPRESSION_DSL_OPTIONS = Object.freeze({
  allowImplicitOr: false,
  allowUnquotedMissingAlias: false,
  dateScopeIssueSeverity: "error",
});

function makeTermNode(term) {
  const normalized = normalizeSearchTerm(term);
  return normalized ? { type: "term", term: normalized } : null;
}

function makeGroupNode(operator, children, negated = false) {
  const normalizedChildren = (Array.isArray(children) ? children : [])
    .filter(Boolean);
  if (!normalizedChildren.length) {
    return null;
  }
  const node = {
    type: "group",
    operator: normalizeSearchExpressionOperator(operator),
    children: normalizedChildren,
  };
  if (negated) {
    node.negated = true;
  }
  return node;
}

function makeContainerNode(expression, negated = false) {
  if (!expression) {
    return null;
  }
  const node = { type: "container", expression };
  if (negated) {
    node.negated = true;
  }
  return node;
}

function searchExpressionIsContainerEntityPredicate(expression) {
  if (!isPlainObject(expression)) {
    return false;
  }
  if (expression.type === "term") {
    return [
      "fish",
      "title",
      "item",
      "item-group",
      "fishing-contest",
      "status",
      "grade",
    ].includes(String(expression.term?.kind || ""));
  }
  if (expression.type !== "group") {
    return false;
  }
  const children = Array.isArray(expression.children) ? expression.children : [];
  return children.length > 0 &&
    children.every((child) => searchExpressionIsContainerEntityPredicate(child));
}

function applyNegation(node) {
  const cloned = cloneSearchExpressionNode(node);
  if (!cloned || searchExpressionNodeHasPatchBound(cloned)) {
    return null;
  }
  if (normalizeSearchExpressionNegated(cloned.negated)) {
    delete cloned.negated;
  } else {
    cloned.negated = true;
  }
  return cloned;
}

function normalizeIssueSeverity(value, fallback = "warning") {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "error" || normalized === "warning") {
    return normalized;
  }
  return fallback;
}

function searchExpressionIssueKey(issue) {
  const span = {
    start: Number.isInteger(issue?.span?.start) ? issue.span.start : 0,
    end: Number.isInteger(issue?.span?.end) ? issue.span.end : 0,
  };
  return [
    String(issue?.stage ?? ""),
    String(issue?.code ?? ""),
    span.start,
    span.end,
    String(issue?.raw ?? ""),
  ].join("|");
}

function mergeSearchExpressionIssues(primaryIssues, secondaryIssues) {
  const merged = [];
  const seen = new Set();
  const primary = Array.isArray(primaryIssues) ? primaryIssues : [];
  const secondary = Array.isArray(secondaryIssues) ? secondaryIssues : [];
  const primaryHasError = primary.some((issue) => issue?.severity === "error");
  for (const issue of primary) {
    const key = searchExpressionIssueKey(issue);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(issue);
  }
  for (const issue of secondary) {
    if (primaryHasError && issue?.code === "expected_term" && String(issue?.raw ?? "") === "") {
      continue;
    }
    const key = searchExpressionIssueKey(issue);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(issue);
  }
  return merged;
}


function normalizeSearchExpressionPrefix(value) {
  return String(value ?? "").trim().toLowerCase().replace(/-/g, "_");
}

function parseLayerFactConditionValue(rawValue) {
  const value = String(rawValue ?? "").trim();
  if (!value) {
    return undefined;
  }
  const first = value[0];
  const last = value[value.length - 1];
  if ((first === "\"" && last === "\"") || (first === "'" && last === "'")) {
    try {
      return first === "\"" ? JSON.parse(value) : value.slice(1, -1).replace(/\\'/g, "'");
    } catch (_error) {
      return value.slice(1, -1);
    }
  }
  if (/^(true|false)$/i.test(value)) {
    return value.toLowerCase() === "true";
  }
  if (/^-?\d+(?:\.\d+)?$/.test(value)) {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }
  return value;
}

function parseLayerFactConditionPath(rawPath) {
  const path = String(rawPath ?? "").trim();
  if (!path) {
    return "";
  }
  const first = path[0];
  const last = path[path.length - 1];
  if ((first === "\"" && last === "\"") || (first === "'" && last === "'")) {
    try {
      const unquoted = first === "\""
        ? JSON.parse(path)
        : path.slice(1, -1).replace(/\\'/g, "'");
      return typeof unquoted === "string" ? unquoted.trim() : "";
    } catch (_error) {
      return path.slice(1, -1).trim();
    }
  }
  return path;
}

function parseLayerFactConditionText(rawText) {
  const text = String(rawText ?? "").trim();
  if (!text) {
    return null;
  }
  const match = text.match(/^([^=!<>~]+?)\s*(>=|<=|!=|==|~=|=|>|<|~)\s*(.+)$/);
  if (!match) {
    return normalizeLayerFactCondition({
      path: parseLayerFactConditionPath(text),
      op: "exists",
    });
  }
  const op = match[2] === "~="
    ? "eq-ci"
    : match[2] === "~"
      ? "contains-ci"
      : normalizeLayerFactConditionOperator(match[2]);
  return normalizeLayerFactCondition({
    path: parseLayerFactConditionPath(match[1]),
    op,
    value: parseLayerFactConditionValue(match[3]),
  });
}

function parseLayerFilterPercent(rawValue) {
  const value = String(rawValue ?? "").trim();
  if (!value.endsWith("%")) {
    return null;
  }
  const match = value.slice(0, -1).match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) {
    return null;
  }
  const whole = Number.parseInt(match[1], 10);
  const fraction = Number.parseInt((match[2] || "").padEnd(2, "0"), 10) || 0;
  return Number.isInteger(whole)
    ? { kind: "percent", basisPoints: whole * 100 + fraction }
    : null;
}

function parseLayerFilterTypedValue(rawValue) {
  const value = String(rawValue ?? "").trim();
  if (!value) {
    return null;
  }
  if (/^(true|false)$/i.test(value)) {
    return { kind: "boolean", value: value.toLowerCase() === "true" };
  }
  if (/^-?\d+(?:\.\d+)?$/.test(value)) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? { kind: "number", value: parsed } : null;
  }
  const percent = parseLayerFilterPercent(value);
  return percent || { kind: "text", value };
}

function normalizeLayerFilterPublicId(value) {
  return String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function semanticLayerForFirstClassPrefix(prefix) {
  const normalized = normalizeSearchExpressionPrefix(prefix);
  if (normalized === "region" || normalized === "regions") {
    return "regions";
  }
  if (
    normalized === "regiongroup" ||
    normalized === "regiongroups" ||
    normalized === "region_group" ||
    normalized === "region_groups"
  ) {
    return "region_groups";
  }
  return "";
}

class SearchExpressionDslParser extends SearchExpressionBooleanParser {
  constructor(input, options = {}) {
    super(input, options);
    this.options = options || {};
    this.dateConstraintSpans = new WeakMap();
    this.includeRawExpressionTree = options.includeRawExpressionTree !== false;
    this.resolvedSpacedValueEnds = this.includeRawExpressionTree ? new Map() : null;
    this.compactedExpression = null;
    this.normalizationOrigins = null;
    this.allowImplicitOr = options.allowImplicitOr !== false;
    this.allowUnquotedMissingAlias = options.allowUnquotedMissingAlias !== false;
    this.dateScopeIssueSeverity = normalizeIssueSeverity(
      options.dateScopeIssueSeverity,
      "warning",
    );
    this.implicitOrIssueSeverity = this.allowImplicitOr
      ? "warning"
      : "error";
    this.implicitOrMessage = this.allowImplicitOr
      ? "Adjacent terms are interpreted as OR. Use an explicit operator."
      : "Adjacent terms are not accepted in strict validation. Use an explicit operator.";
  }

  makeGroupNode(operator, children, negated = false) {
    return makeGroupNode(operator, children, negated);
  }

  supportsContainerScope() {
    return true;
  }

  makeContainerNode(expression, openToken, closeToken) {
    if (!searchExpressionIsContainerEntityPredicate(expression)) {
      this.addIssue({
        stage: "semantic",
        severity: "error",
        code: "invalid_container_scope",
        span: {
          start: openToken?.start ?? this.previousEnd(),
          end: closeToken?.end ?? this.previousEnd(),
        },
        message: "Container brackets can contain only fish, item, contest, grade, or status predicates.",
      });
    }
    return makeContainerNode(expression);
  }

  handleUnterminatedContainer(openToken) {
    this.addIssue({
      code: "unterminated_container",
      span: { start: openToken?.start ?? 0, end: this.previousEnd() },
      expected: ["]"],
      message: "Expected a closing container bracket.",
    });
  }

  addCatalogReferenceIssue(resolveResult, token, unknownCode, label) {
    if (!resolveResult || resolveResult.status === "resolved") {
      return;
    }
    const code = resolveResult.status === "catalog_unavailable"
      ? "catalog_unavailable"
      : unknownCode;
    const lowerLabel = String(label || "catalog").toLowerCase();
    this.addIssue({
      stage: "resolve",
      severity: "error",
      code,
      span: spanForSearchExpressionToken(token, this.previousEnd()),
      message: code === "catalog_unavailable"
        ? `${label} names require a loaded catalog. Use a numeric ID or retry after catalog data loads.`
        : `Unknown ${lowerLabel} reference.`,
    });
  }

  addSemanticReferenceIssue(resolveResult, layerToken, fieldToken) {
    if (!resolveResult || resolveResult.status === "resolved") {
      return;
    }
    let code = "unknown_semantic_field";
    let token = fieldToken;
    let message = "Unknown semantic field reference.";
    if (resolveResult.status === "catalog_unavailable") {
      code = "catalog_unavailable";
      message = "Semantic field names require a loaded catalog. Use a numeric field ID or retry after catalog data loads.";
    } else if (resolveResult.status === "unknown_layer") {
      code = "unknown_semantic_layer";
      token = layerToken;
      message = "Unknown semantic layer reference.";
    }
    this.addIssue({
      stage: "resolve",
      severity: "error",
      code,
      span: spanForSearchExpressionToken(token, this.previousEnd()),
      message,
    });
  }

  addUnknownSourceKindIssue(token) {
    this.addIssue({
      stage: "resolve",
      severity: "error",
      code: "unknown_source_kind",
      span: spanForSearchExpressionToken(token, this.previousEnd()),
      message: "Unknown item source kind.",
    });
  }

  addUnknownTermIssue(token) {
    this.addIssue({
      stage: "resolve",
      severity: "error",
      code: "unknown_term",
      span: spanForSearchExpressionToken(token, this.previousEnd()),
      message: "Unknown search term.",
    });
  }

  addInvalidPatchIdIssue(prefixToken, valueToken = null) {
    this.addIssue({
      stage: "resolve",
      severity: "error",
      code: "invalid_patch_id",
      span: {
        start: prefixToken?.start ?? valueToken?.start ?? this.previousEnd(),
        end: valueToken?.end ?? this.previousEnd(),
      },
      message: "Patch/date constraints require a patch ID.",
    });
  }

  addDateScopeIssue(node) {
    this.addIssue({
      stage: "semantic",
      severity: this.dateScopeIssueSeverity,
      code: "date_bound_without_evidence_scope",
      span: this.dateConstraintSpans.get(node) || this.currentSpan(),
      message: "Patch/date constraints require an evidence-sample scope.",
    });
  }

  finalizeParsedExpression(expression) {
    this.warnDateConstraintsWithoutEvidenceScope(expression);
    if (this.hasErrorIssues()) {
      return null;
    }
    const compacted = compactSearchExpressionNode(expression, { isRoot: true }) || expression;
    if (!this.includeRawExpressionTree) {
      return normalizeSearchExpression(compacted) || buildSearchExpressionFromSelectedTerms([]);
    }
    const normalized = normalizeSearchExpressionWithOrigins(compacted);
    this.compactedExpression = compacted;
    this.normalizationOrigins = normalized.origins;
    return normalized.expression || buildSearchExpressionFromSelectedTerms([]);
  }

  warnDateConstraintsWithoutEvidenceScope(expression) {
    const reported = new Set();
    const report = (dateNode) => {
      const span = this.dateConstraintSpans.get(dateNode) || this.currentSpan();
      const key = `${span.start}:${span.end}`;
      if (reported.has(key)) {
        return;
      }
      reported.add(key);
      this.addDateScopeIssue(dateNode);
    };
    const visit = (node, context = {}) => {
      if (!isPlainObject(node)) {
        return;
      }
      if (node.type === "term") {
        if (
          searchExpressionNodeHasEvidenceDateConstraint(node) &&
          context.hasConjunctNonDateTerm === true &&
          context.hasConjunctEvidenceScope !== true
        ) {
          report(node);
        }
        return;
      }
      if (node.type === "container") {
        visit(node.expression, context);
        return;
      }
      if (node.type !== "group") {
        return;
      }
      const children = Array.isArray(node.children) ? node.children : [];
      if (normalizeSearchExpressionOperator(node.operator) !== "and") {
        for (const child of children) {
          visit(child, context);
        }
        return;
      }
      const childHasNonDateTerm = children.map((child) =>
        searchExpressionNodeHasNonDateTerm(child)
      );
      const childProvidesEvidenceScope = children.map((child) =>
        searchExpressionNodeProvidesEvidenceSampleScopeForEveryBranch(child)
      );
      for (let index = 0; index < children.length; index += 1) {
        const hasConjunctNonDateTerm = context.hasConjunctNonDateTerm === true ||
          childHasNonDateTerm.some((value, otherIndex) => otherIndex !== index && value);
        const hasConjunctEvidenceScope = context.hasConjunctEvidenceScope === true ||
          childProvidesEvidenceScope.some((value, otherIndex) => otherIndex !== index && value);
        visit(children[index], {
          hasConjunctNonDateTerm,
          hasConjunctEvidenceScope,
        });
      }
    };
    visit(expression);
  }

  parsePrefixAliasExpression(negated) {
    const aliasLength = this.allowUnquotedMissingAlias
      ? searchExpressionMissingAliasLength(this.tokens, this.index)
      : 0;
    if (!aliasLength) {
      return null;
    }
    this.index += aliasLength;
    const aliasNode = makeTermNode({ kind: "status", status: "missing" });
    return negated ? applyNegation(aliasNode) : aliasNode;
  }

  applyParsedNegation(node, negationStart) {
    if (searchExpressionNodeHasPatchBound(node)) {
      this.addIssue({
        stage: "semantic",
        code: "date_bound_inside_unsupported_negation",
        span: { start: negationStart ?? 0, end: this.previousEnd() },
        message: "Patch/date constraints cannot be negated in the canonical v1 DSL.",
      });
      return null;
    }
    return applyNegation(node);
  }

  parseValue() {
    const token = this.parseValueToken();
    return token ? searchExpressionTokenText(token).trim() : "";
  }

  parseSourceKinds() {
    if (!this.matchSymbol("@")) {
      return [];
    }
    const tokens = [];
    const first = this.parseValueToken();
    if (!first) {
      return [];
    }
    tokens.push(first);
    while (this.matchSymbol(",")) {
      const next = this.parseValueToken();
      if (!next) {
        return [];
      }
      tokens.push(next);
    }
    const values = [];
    let hasUnknownSourceKind = false;
    for (const token of tokens) {
      const value = searchExpressionTokenText(token);
      if (!isKnownSourceKind(value)) {
        hasUnknownSourceKind = true;
        this.addUnknownSourceKindIssue(token);
        continue;
      }
      values.push(normalizeSourceKind(value));
    }
    return hasUnknownSourceKind ? null : normalizeSourceKinds(values);
  }

  parseLayerFactConditions() {
    if (!this.matchSymbol("[")) {
      return [];
    }
    const conditions = [];
    if (this.matchSymbol("]")) {
      return conditions;
    }
    while (this.current()) {
      const firstToken = this.current();
      const start = firstToken?.start ?? this.previousEnd();
      let end = start;
      while (this.current()) {
        const token = this.current();
        if (token.type === "symbol" && (token.value === "," || token.value === "]")) {
          break;
        }
        end = token.end;
        this.index += 1;
      }
      const condition = parseLayerFactConditionText(this.source.slice(start, end));
      if (!condition) {
        this.addIssue({
          stage: "parse",
          severity: "error",
          code: "unexpected_token",
          span: { start, end },
          message: "Expected a layer fact condition.",
        });
        return null;
      }
      conditions.push(condition);
      if (this.matchSymbol("]")) {
        return normalizeLayerFactConditions(conditions);
      }
      if (!this.matchSymbol(",")) {
        this.addIssue({
          stage: "parse",
          severity: "error",
          code: "unexpected_token",
          span: this.currentSpan(),
          expected: [",", "]"],
          message: "Expected a comma or closing bracket in layer fact conditions.",
        });
        return null;
      }
    }
    this.addIssue({
      stage: "parse",
      severity: "error",
      code: "unterminated_group",
      span: { start: this.previousEnd(), end: this.previousEnd() },
      expected: ["]"],
      message: "Expected a closing bracket in layer fact conditions.",
    });
    return null;
  }

  parseBracketBody(label = "bracketed expression") {
    const openToken = this.current();
    if (!this.matchSymbol("[")) {
      this.addIssue({
        stage: "parse",
        severity: "error",
        code: "unexpected_token",
        span: this.currentSpan(),
        expected: ["["],
        message: `Expected ${label}.`,
      });
      return null;
    }
    const contentStart = openToken.end;
    let depth = 1;
    while (this.current()) {
      const token = this.current();
      if (token.type === "symbol" && token.value === "[") {
        depth += 1;
      } else if (token.type === "symbol" && token.value === "]") {
        depth -= 1;
        if (depth === 0) {
          const contentEnd = token.start;
          this.index += 1;
          return {
            text: this.source.slice(contentStart, contentEnd),
            span: { start: contentStart, end: contentEnd },
            end: token.end,
          };
        }
      }
      this.index += 1;
    }
    this.addIssue({
      stage: "parse",
      severity: "error",
      code: "unterminated_group",
      span: { start: openToken.start, end: this.previousEnd() },
      expected: ["]"],
      message: `Expected a closing bracket for ${label}.`,
    });
    return null;
  }

  addOffsetIssues(issues, offset) {
    for (const issue of Array.isArray(issues) ? issues : []) {
      const span = {
        start: offset + (Number.isInteger(issue?.span?.start) ? issue.span.start : 0),
        end: offset + (Number.isInteger(issue?.span?.end) ? issue.span.end : 0),
      };
      this.addIssue({
        stage: issue?.stage || "parse",
        severity: issue?.severity || "error",
        code: issue?.code || "unexpected_token",
        span,
        expected: issue?.expected,
        message: issue?.message || "Invalid layer filter predicate.",
      });
    }
  }

  makePatchBoundTerm(bound, patchId, span) {
    const node = makeTermNode({ kind: "patch-bound", bound, patchId });
    if (node && span) {
      this.dateConstraintSpans.set(node, span);
    }
    return node;
  }

  resolveSpacedValueToken(firstToken, resolveCandidate) {
    const selected = super.resolveSpacedValueToken(firstToken, resolveCandidate);
    if (selected?.resolved?.status === "resolved") {
      this.resolvedSpacedValueEnds?.set(firstToken.start, selected.token.end);
    }
    return selected;
  }

  resolveUnprefixedCatalogTerm(rawValue) {
    const value = String(rawValue ?? "").trim();
    if (!value || parseSearchExpressionPositiveInteger(value) != null || parseSearchExpressionZoneRgb(value) != null) {
      return { status: "invalid" };
    }
    const resolved = resolveSearchExpressionUnprefixedCatalogTerm(value, this.options);
    if (resolved.status === "resolved" || resolved.status === "ambiguous") return resolved;
    const title = resolveFishingTitleReference(value, this.options);
    return title.status === "resolved" || title.status === "ambiguous" ? title : resolved;
  }

  parsePrefixedTerm(prefix, prefixToken) {
    if (!this.matchSymbol(":")) {
      return null;
    }
    if (prefix === "filter") {
      const layerToken = this.parseValueToken();
      if (!layerToken) {
        return null;
      }
      const layerId = normalizeLayerFilterId(searchExpressionTokenText(layerToken));
      const body = this.parseBracketBody("layer filter predicate");
      if (!body || !layerId) {
        return null;
      }
      const parser = new LayerFilterDslParser(body.text, this.options);
      const predicate = parser.parse();
      this.addOffsetIssues(parser.issues, body.span.start);
      return parser.hasErrorIssues() || !predicate
        ? null
        : makeTermNode({ kind: "layer-filter", layerId, predicate });
    }
    if (prefix === "title") {
      const token = this.parseValueToken();
      if (!token) return null;
      const value = this.resolveSpacedValueToken(token, raw => resolveFishingTitleReference(raw, this.options));
      const resolved = value?.resolved || resolveFishingTitleReference(searchExpressionTokenText(token), this.options);
      if (resolved.status !== "resolved") {
        this.addCatalogReferenceIssue(resolved, value?.token || token, "unknown_title", "Title");
        return null;
      }
      return makeTermNode(resolved.term);
    }
    if (prefix === "fish" || prefix === "item") {
      const firstValueToken = this.parseValueToken();
      if (!firstValueToken) {
        return null;
      }
      const value = this.resolveSpacedValueToken(firstValueToken, (rawValue) =>
        resolveSearchExpressionFishOrItemReference(rawValue, this.options, prefix)
      );
      const valueToken = value?.token || firstValueToken;
      const resolved = value?.resolved || resolveSearchExpressionFishOrItemReference(
        searchExpressionTokenText(valueToken),
        this.options,
        prefix,
      );
      if (resolved.status !== "resolved") {
        this.addCatalogReferenceIssue(
          resolved,
          valueToken,
          prefix === "fish" ? "unknown_fish" : "unknown_item",
          prefix === "fish" ? "Fish" : "Item",
        );
        return null;
      }
      const sourceKinds = this.parseSourceKinds();
      if (sourceKinds == null) {
        return null;
      }
      if (resolved.kind === "item-group") {
        const resolvedSourceKinds = normalizeSourceKinds(resolved.sourceKinds);
        const nextSourceKinds = sourceKinds.length ? sourceKinds : resolvedSourceKinds;
        return makeTermNode({
          kind: "item-group",
          itemIds: resolved.itemIds,
          sourceKinds: nextSourceKinds,
        });
      }
      if (resolved.kind === "fish" && sourceKinds.length === 0) {
        return makeTermNode({ kind: "fish", fishId: resolved.id });
      }
      return makeTermNode({ kind: "item", itemId: resolved.id, sourceKinds });
    }
    if (prefix === "npc") {
      const firstValueToken = this.parseValueToken();
      if (!firstValueToken) {
        return null;
      }
      const value = this.resolveSpacedValueToken(firstValueToken, (rawValue) =>
        resolveSearchExpressionTradeNpcKey(rawValue, this.options)
      );
      const valueToken = value?.token || firstValueToken;
      const resolved = value?.resolved || resolveSearchExpressionTradeNpcKey(
        searchExpressionTokenText(valueToken),
        this.options,
      );
      if (resolved.status !== "resolved") {
        this.addCatalogReferenceIssue(
          resolved,
          valueToken,
          "unknown_trade_npc",
          "Trade NPC",
        );
        return null;
      }
      return makeTermNode(tradeNpcSearchTermForKey(resolved.npcKey));
    }
    if (prefix === "items") {
      const valueTokens = this.parseValueListTokens();
      if (!valueTokens) {
        return null;
      }
      const itemIds = [];
      let hasUnresolvedItem = false;
      for (const valueToken of valueTokens) {
        const resolved = resolveSearchExpressionItemId(
          searchExpressionTokenText(valueToken),
          this.options,
        );
        if (resolved.status === "resolved") {
          itemIds.push(resolved.id);
          continue;
        }
        hasUnresolvedItem = true;
        this.addCatalogReferenceIssue(resolved, valueToken, "unknown_item", "Item");
      }
      if (hasUnresolvedItem) {
        return null;
      }
      const sourceKinds = this.parseSourceKinds();
      if (sourceKinds == null) {
        return null;
      }
      return makeTermNode({ kind: "item-group", itemIds, sourceKinds });
    }
    if (prefix === "zone" || prefix === "rod") {
      const firstValueToken = this.parseValueToken();
      if (!firstValueToken) {
        return null;
      }
      const value = this.resolveSpacedValueToken(firstValueToken, (rawValue) =>
        resolveSearchExpressionZoneReference(rawValue, this.options, { multiple: prefix === "zone" })
      );
      const valueToken = value?.token || firstValueToken;
      const resolved = value?.resolved || resolveSearchExpressionZoneReference(
        searchExpressionTokenText(valueToken),
        this.options,
        { multiple: prefix === "zone" },
      );
      if (resolved.status !== "resolved") {
        this.addCatalogReferenceIssue(resolved, valueToken, "unknown_zone", "Zone");
        return null;
      }
      const nodes = (resolved.zoneRgbs ?? [resolved.zoneRgb])
        .map(zoneRgb => makeTermNode({ kind: "zone", zoneRgb }));
      return nodes.length === 1 ? nodes[0] : makeGroupNode("or", nodes);
    }
    const firstClassSemanticLayer = semanticLayerForFirstClassPrefix(prefix);
    if (firstClassSemanticLayer) {
      const firstValueToken = this.parseValueToken();
      if (!firstValueToken) {
        return null;
      }
      const value = this.resolveSpacedValueToken(firstValueToken, (rawValue) =>
        resolveSearchExpressionSemanticField(firstClassSemanticLayer, rawValue, this.options)
      );
      const valueToken = value?.token || firstValueToken;
      const resolved = value?.resolved || resolveSearchExpressionSemanticField(
        firstClassSemanticLayer,
        searchExpressionTokenText(valueToken),
        this.options,
      );
      if (resolved.status !== "resolved") {
        this.addSemanticReferenceIssue(resolved, prefixToken, valueToken);
        return null;
      }
      return makeTermNode({
        kind: "semantic",
        layerId: resolved.layerId,
        fieldId: resolved.fieldId,
      });
    }
    if (prefix === "semantic") {
      const layerToken = this.parseValueToken();
      if (!layerToken || !this.matchSymbol(":")) {
        return null;
      }
      const fieldToken = this.parseValueToken();
      if (!fieldToken) {
        return null;
      }
      const resolved = resolveSearchExpressionSemanticField(
        searchExpressionTokenText(layerToken),
        searchExpressionTokenText(fieldToken),
        this.options,
      );
      if (resolved.status === "resolved") {
        return makeTermNode({
          kind: "semantic",
          layerId: resolved.layerId,
          fieldId: resolved.fieldId,
        });
      }
      this.addSemanticReferenceIssue(resolved, layerToken, fieldToken);
      return null;
    }
    if (prefix === "fact") {
      const layerToken = this.parseValueToken();
      if (!layerToken || !this.matchSymbol(":")) {
        return null;
      }
      const factToken = this.parseValueToken();
      if (!factToken) {
        return null;
      }
      const layerId = normalizeLayerFactId(searchExpressionTokenText(layerToken));
      const factId = normalizeLayerFactId(searchExpressionTokenText(factToken));
      const conditions = this.parseLayerFactConditions();
      if (conditions == null) {
        return null;
      }
      return layerId && factId
        ? makeTermNode({ kind: "layer-fact", layerId, factId, conditions })
        : null;
    }
    if (prefix === "from" || prefix === "to" || prefix === "before" || prefix === "after") {
      const bound = normalizePatchBound(prefix);
      const valueToken = this.parseValueToken();
      if (!valueToken) {
        this.addInvalidPatchIdIssue(prefixToken);
        return null;
      }
      const resolved = resolveSearchExpressionPatchId(
        searchExpressionTokenText(valueToken),
        this.options,
      );
      if (resolved.status !== "resolved") {
        if (resolved.status === "invalid") {
          this.addInvalidPatchIdIssue(prefixToken, valueToken);
        } else {
          this.addCatalogReferenceIssue(resolved, valueToken, "invalid_patch_id", "Patch");
        }
        return null;
      }
      const patchId = resolved.patchId;
      return bound && patchId
        ? this.makePatchBoundTerm(bound, patchId, {
            start: prefixToken?.start ?? valueToken?.start ?? this.previousEnd(),
            end: valueToken?.end ?? this.previousEnd(),
          })
        : null;
    }
    if (prefix === "patch") {
      const valueToken = this.parseValueToken();
      if (!valueToken) {
        this.addInvalidPatchIdIssue(prefixToken);
        return null;
      }
      const resolved = resolveSearchExpressionPatchId(
        searchExpressionTokenText(valueToken),
        this.options,
      );
      if (resolved.status !== "resolved") {
        if (resolved.status === "invalid") {
          this.addInvalidPatchIdIssue(prefixToken, valueToken);
        } else {
          this.addCatalogReferenceIssue(resolved, valueToken, "invalid_patch_id", "Patch");
        }
        return null;
      }
      const patchId = resolved.patchId;
      const span = {
        start: prefixToken?.start ?? valueToken?.start ?? this.previousEnd(),
        end: valueToken?.end ?? this.previousEnd(),
      };
      return makeGroupNode("and", [
        this.makePatchBoundTerm("from", patchId, span),
        this.makePatchBoundTerm("to", patchId, span),
      ]);
    }
    if (prefix === "status") {
      const valueToken = this.parseValueToken();
      if (!valueToken) {
        return null;
      }
      const status = normalizeSearchStatusTerm(searchExpressionTokenText(valueToken));
      return status ? makeTermNode({ kind: "status", status }) : null;
    }
    if (prefix === "grade") {
      const valueToken = this.parseValueToken();
      if (!valueToken) {
        return null;
      }
      const grade = normalizeSearchGradeTerm(searchExpressionTokenText(valueToken));
      return grade ? makeTermNode({ kind: "grade", grade }) : null;
    }
    return null;
  }

  parseTerm() {
    const token = this.current();
    if (token?.type !== "word" && token?.type !== "value") {
      return null;
    }
    const valueToken = this.parseValueToken();
    const value = searchExpressionTokenText(valueToken).trim();
    const lowered = normalizeSearchExpressionPrefix(value);
    if (this.current()?.type === "symbol" && this.current().value === ":") {
      return this.parsePrefixedTerm(lowered, valueToken);
    }
    if (lowered === "contest") {
      return makeTermNode({ kind: "fishing-contest" });
    }
    const afterFirstValueIndex = this.index;
    const catalogCandidate = this.resolveSpacedValueToken(
      valueToken,
      (rawValue) => this.resolveUnprefixedCatalogTerm(rawValue),
    );
    const catalogCandidateIsSpaced = (catalogCandidate?.nextIndex ?? afterFirstValueIndex) > afterFirstValueIndex;
    if (catalogCandidateIsSpaced && catalogCandidate?.resolved?.status === "resolved") {
      return makeTermNode(catalogCandidate.resolved.term);
    }
    this.index = afterFirstValueIndex;
    const fishFilterTerm = normalizeFishFilterTerm(value);
    if (fishFilterTerm) {
      const status = normalizeSearchStatusTerm(fishFilterTerm);
      if (status) {
        return makeTermNode({ kind: "status", status });
      }
      const grade = normalizeSearchGradeTerm(fishFilterTerm);
      if (grade) {
        return makeTermNode({ kind: "grade", grade });
      }
    }
    const prefixFilterTerm = normalizeFishFilterPrefixTerm(value);
    if (prefixFilterTerm) {
      const status = normalizeSearchStatusTerm(prefixFilterTerm);
      if (status) {
        return makeTermNode({ kind: "status", status });
      }
      const grade = normalizeSearchGradeTerm(prefixFilterTerm);
      if (grade) {
        return makeTermNode({ kind: "grade", grade });
      }
    }
    if (catalogCandidate?.resolved?.status === "resolved") {
      return makeTermNode(catalogCandidate.resolved.term);
    }
    if (
      catalogCandidate?.resolved?.status === "catalog_unavailable" &&
      this.allowUnquotedMissingAlias &&
      /[\p{L}\p{N}]/u.test(value)
    ) {
      this.addCatalogReferenceIssue(
        catalogCandidate.resolved,
        catalogCandidate.token || valueToken,
        "unknown_term",
        "Search term",
      );
      return null;
    }
    this.addUnknownTermIssue(valueToken);
    return null;
  }
}

class LayerFilterDslParser extends SearchExpressionDslParser {
  constructor(input, options = {}) {
    super(input, options);
    this.implicitOrMessage = "Adjacent layer predicates are interpreted as OR. Use an explicit operator.";
  }

  makeGroupNode(operator, children, negated = false) {
    const normalizedChildren = (Array.isArray(children) ? children : []).filter(Boolean);
    if (!normalizedChildren.length) {
      return null;
    }
    const predicate = {
      kind: "bool",
      op: normalizeSearchExpressionOperator(operator),
      children: normalizedChildren,
    };
    if (negated) {
      predicate.negated = true;
    }
    return predicate;
  }

  finalizeParsedExpression(expression) {
    return expression;
  }

  parsePrefixAliasExpression(_negated) {
    return null;
  }

  applyParsedNegation(node, _negationStart) {
    return node ? { ...node, negated: node.negated !== true } : null;
  }

  handleUnterminatedGroup(openToken) {
    this.addIssue({
      stage: "parse",
      severity: "error",
      code: "unterminated_group",
      span: { start: openToken?.start ?? 0, end: this.previousEnd() },
      expected: [")"],
      message: "Expected a closing parenthesis in layer filter predicate.",
    });
  }

  layerReservedToken(token) {
    if (token?.type !== "word") {
      return false;
    }
    const lowered = searchExpressionTokenLower(token);
    return lowered === "and" ||
      lowered === "or" ||
      lowered === "not" ||
      lowered === "in" ||
      lowered === "exists" ||
      lowered === "where" ||
      Boolean(normalizeLayerFilterCompareOperator(lowered));
  }

  layerSpacedValueCandidates(firstToken) {
    const initialIndex = this.index;
    const candidates = [
      {
        token: firstToken,
        nextIndex: initialIndex,
      },
    ];
    if (firstToken?.type !== "word") {
      return candidates;
    }
    let lastToken = firstToken;
    let scanIndex = initialIndex;
    let previousEnd = firstToken.end;
    while (scanIndex < this.tokens.length) {
      const token = this.tokens[scanIndex];
      const gap = this.source.slice(previousEnd, token.start);
      if (
        /[\n()[\]]/.test(gap) ||
        !searchExpressionTokenIsValue(token) ||
        this.layerReservedToken(token)
      ) {
        break;
      }
      if (
        token.type === "word" &&
        this.tokens[scanIndex + 1]?.type === "symbol" &&
        this.tokens[scanIndex + 1].value === ":"
      ) {
        break;
      }
      lastToken = token;
      scanIndex += 1;
      previousEnd = token.end;
      candidates.push({
        token: makeCombinedSearchExpressionValueToken(this.source, firstToken, lastToken),
        nextIndex: scanIndex,
      });
    }
    return candidates;
  }

  resolveLayerSpacedValueToken(firstToken, resolveCandidate) {
    const candidates = this.layerSpacedValueCandidates(firstToken);
    const resolvedCandidates = candidates.map((candidate) => ({
      ...candidate,
      resolved: resolveCandidate(searchExpressionTokenText(candidate.token)),
    }));
    const resolvedCandidate = [...resolvedCandidates]
      .reverse()
      .find((candidate) => candidate.resolved.status === "resolved");
    const selected = resolvedCandidate || resolvedCandidates[0];
    this.index = selected?.nextIndex ?? this.index;
    return selected;
  }

  parseSubjectRef() {
    const startIndex = this.index;
    const prefixToken = this.current();
    if (prefixToken?.type !== "word") {
      return null;
    }
    const prefix = normalizeSearchExpressionPrefix(searchExpressionTokenText(prefixToken));
    if (prefix !== "fish" && prefix !== "item" && prefix !== "zone") {
      return null;
    }
    this.index += 1;
    if (!this.matchSymbol(":")) {
      this.index = startIndex;
      return null;
    }
    const firstValueToken = this.parseValueToken();
    if (!firstValueToken) {
      this.addIssue({
        stage: "parse",
        severity: "error",
        code: "expected_term",
        span: this.currentSpan(),
        message: "Expected a layer filter subject reference.",
      });
      return null;
    }
    if (prefix === "zone") {
      const value = this.resolveLayerSpacedValueToken(firstValueToken, (rawValue) =>
        resolveSearchExpressionZoneReference(rawValue, this.options)
      );
      const valueToken = value?.token || firstValueToken;
      const resolved = value?.resolved || resolveSearchExpressionZoneReference(
        searchExpressionTokenText(valueToken),
        this.options,
      );
      if (resolved.status !== "resolved") {
        this.addCatalogReferenceIssue(resolved, valueToken, "unknown_zone", "Zone");
        return null;
      }
      return { kind: "zone", zoneRgb: resolved.zoneRgb };
    }
    const value = this.resolveLayerSpacedValueToken(firstValueToken, (rawValue) =>
      resolveSearchExpressionFishOrItemReference(rawValue, this.options, prefix)
    );
    const valueToken = value?.token || firstValueToken;
    const resolved = value?.resolved || resolveSearchExpressionFishOrItemReference(
      searchExpressionTokenText(valueToken),
      this.options,
      prefix,
    );
    if (resolved.status !== "resolved") {
      this.addCatalogReferenceIssue(
        resolved,
        valueToken,
        prefix === "fish" ? "unknown_fish" : "unknown_item",
        prefix === "fish" ? "Fish" : "Item",
      );
      return null;
    }
    if (resolved.kind === "item-group") {
      return { kind: "item-group", itemIds: resolved.itemIds };
    }
    return resolved.kind === "fish"
      ? { kind: "fish", fishId: resolved.id }
      : { kind: "item", itemId: resolved.id };
  }

  parseGroupRef() {
    const token = this.parseValueToken();
    const group = normalizeLayerFilterPublicId(searchExpressionTokenText(token));
    if (!group) {
      this.addIssue({
        stage: "parse",
        severity: "error",
        code: "expected_term",
        span: this.currentSpan(),
        message: "Expected a layer group name.",
      });
      return "";
    }
    return group;
  }

  parseCompareOperator() {
    const token = this.current();
    if (!searchExpressionTokenIsValue(token)) {
      return null;
    }
    const op = normalizeLayerFilterCompareOperator(searchExpressionTokenText(token));
    if (!op) {
      return null;
    }
    this.index += 1;
    return { op, token };
  }

  parseRateComparison() {
    const compare = this.parseCompareOperator();
    if (!compare) {
      return null;
    }
    const valueToken = this.parseValueToken();
    if (!valueToken) {
      this.addIssue({
        stage: "parse",
        severity: "error",
        code: "expected_term",
        span: this.currentSpan(),
        message: "Expected a percent value.",
      });
      return null;
    }
    const value = parseLayerFilterPercent(searchExpressionTokenText(valueToken));
    if (!value) {
      this.addIssue({
        stage: "semantic",
        severity: "error",
        code: "expected_percent",
        span: spanForSearchExpressionToken(valueToken, this.previousEnd()),
        message: "Rate comparisons require a percent value such as `30%`.",
      });
      return null;
    }
    return { op: compare.op, value };
  }

  parseSubjectPredicate() {
    const subject = this.parseSubjectRef();
    if (!subject) {
      return null;
    }
    let membershipOp = "";
    if (
      searchExpressionTokenLower(this.current()) === "not" &&
      searchExpressionTokenLower(this.tokens[this.index + 1]) === "in"
    ) {
      this.index += 2;
      membershipOp = "not-in";
    } else if (this.matchWord("in")) {
      membershipOp = "in";
    }
    if (!membershipOp) {
      return { kind: "subject-presence", subject };
    }
    const group = this.parseGroupRef();
    if (!group) {
      return null;
    }
    if (membershipOp === "not-in") {
      if (this.parseCompareOperator()) {
        this.addIssue({
          stage: "semantic",
          severity: "error",
          code: "negative_membership_rate",
          span: this.currentSpan(),
          message: "`NOT IN` predicates cannot also compare a drop rate.",
        });
        return null;
      }
      return { kind: "membership", subject, group, op: membershipOp };
    }
    const rate = this.parseRateComparison();
    return rate
      ? { kind: "membership", subject, group, op: membershipOp, rate }
      : { kind: "membership", subject, group, op: membershipOp };
  }

  parseGroupRatePredicate() {
    const startIndex = this.index;
    const token = this.current();
    if (!searchExpressionTokenIsValue(token)) {
      return null;
    }
    let group = "";
    if (
      token.type === "word" &&
      normalizeSearchExpressionPrefix(searchExpressionTokenText(token)) === "group" &&
      this.tokens[this.index + 1]?.type === "symbol" &&
      this.tokens[this.index + 1].value === ":"
    ) {
      this.index += 2;
      group = this.parseGroupRef();
    } else if (searchExpressionTokenLower(this.tokens[this.index + 1]) === "group") {
      this.index += 1;
      group = normalizeLayerFilterPublicId(searchExpressionTokenText(token));
      this.index += 1;
    }
    if (!group) {
      this.index = startIndex;
      return null;
    }
    const comparison = this.parseRateComparison();
    if (!comparison) {
      this.addIssue({
        stage: "parse",
        severity: "error",
        code: "expected_term",
        span: this.currentSpan(),
        message: "Expected a percent comparison for the layer group.",
      });
      return null;
    }
    return { kind: "group-rate", group, comparison };
  }

  parseRelationPredicate() {
    const relationToken = this.current();
    if (relationToken?.type !== "word") {
      return null;
    }
    const relation = normalizeLayerFilterPublicId(searchExpressionTokenText(relationToken));
    if (relation !== "inside") {
      return null;
    }
    this.index += 1;
    const target = this.parseSubjectRef();
    if (!target) {
      this.addIssue({
        stage: "parse",
        severity: "error",
        code: "expected_term",
        span: this.currentSpan(),
        message: "Expected a relation target.",
      });
      return null;
    }
    return { kind: "relation", relation, target };
  }

  parsePropertyComparePredicate() {
    const startIndex = this.index;
    const fieldToken = this.parseValueToken();
    if (!fieldToken) {
      return null;
    }
    const compare = this.parseCompareOperator();
    if (!compare) {
      this.index = startIndex;
      return null;
    }
    const valueToken = this.parseValueToken();
    if (!valueToken) {
      this.addIssue({
        stage: "parse",
        severity: "error",
        code: "expected_term",
        span: this.currentSpan(),
        message: "Expected a comparison value.",
      });
      return null;
    }
    const field = normalizeLayerFilterPublicId(searchExpressionTokenText(fieldToken));
    const value = parseLayerFilterTypedValue(searchExpressionTokenText(valueToken));
    return field && value
      ? { kind: "property-compare", field, op: compare.op, value }
      : null;
  }

  parseTerm() {
    const predicate = this.parseGroupRatePredicate() ||
      this.parseRelationPredicate() ||
      this.parseSubjectPredicate() ||
      this.parsePropertyComparePredicate();
    if (predicate) {
      return predicate;
    }
    this.addIssue({
      stage: "parse",
      severity: "error",
      code: "unknown_layer_predicate",
      span: this.currentSpan(),
      message: "Unknown layer filter predicate.",
    });
    return null;
  }
}

function expressionIsSingleTerm(expression) {
  if (!expression || typeof expression !== "object") {
    return false;
  }
  if (expression.type === "term") {
    return true;
  }
  if (expression.type === "container") {
    return expressionIsSingleTerm(expression.expression);
  }
  return expression.type === "group" &&
    Array.isArray(expression.children) &&
    expression.children.length === 1 &&
    expressionIsSingleTerm(expression.children[0]);
}

export function parseSearchExpressionDslResult(input, options = {}) {
  const source = String(input ?? "");
  if (!source.trim()) {
    return {
      expression: null,
      rawExpressionTree: null,
      issues: [],
      leadingExpressionEnd: null,
    };
  }
  const parser = new SearchExpressionDslParser(source, options);
  const expression = parser.parse();
  const includeRawExpressionTree = options.includeRawExpressionTree !== false;
  const rawResult = includeRawExpressionTree
    ? parseResolvedRawSearchExpressionDslResult(
        source,
        {
          resolvedSpacedValueEnds: parser.resolvedSpacedValueEnds,
          allowUnquotedMissingAlias: parser.allowUnquotedMissingAlias,
        },
      )
    : { expression: null, issues: [] };
  if (rawResult.expression && expression && parser.compactedExpression) {
    const bindings = projectNormalizedSearchExpressionSourceBindings(
      source,
      rawResult.expression,
      parser.compactedExpression,
      expression,
      parser.normalizationOrigins,
    );
    rawResult.expression.resolvedBindings =
      serializeSearchExpressionSourceBindings(bindings, source, expression);
  }
  const rawIssues = expressionIsSingleTerm(expression)
    ? rawResult.issues.filter((issue) => issue?.code !== "implicit_or")
    : rawResult.issues;
  return {
    expression,
    rawExpressionTree: rawResult.expression,
    issues: mergeSearchExpressionIssues(parser.issues, rawIssues),
    leadingExpressionEnd: parser.leadingExpressionEnd,
  };
}

export function parseSearchExpressionDsl(input, options = {}) {
  return parseSearchExpressionDslResult(input, options).expression;
}

export function validateSearchExpressionDslResult(input, options = {}) {
  return parseSearchExpressionDslResult(input, {
    ...options,
    ...STRICT_SEARCH_EXPRESSION_DSL_OPTIONS,
  });
}

export function validateSearchExpressionDsl(input, options = {}) {
  return validateSearchExpressionDslResult(input, options).expression;
}
